import React, { FC } from "react";
import { LoadingSpinner } from "../../../../../components";
import type { ProfileLinkConfig } from "../../../../../client";
import { DumbDropdownButtonOption } from "../../../../../components/buttons/dumb-dropdown-button";
import { BulletSectionTitle } from "../../../../../components/section/BulletSectionTitle";
import { cn } from "../../../../../components/utils/cn";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../../components/popover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faCircleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { LeadRowCard } from "../card/LeadRowCard";
import MagicIcon from "../../../../../assets/magic_icon.png";

const capitalizeFirst = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

interface LanguageLinkSettingsProps {
  linkConfig?: ProfileLinkConfig;
  onUpdate: (payload: Partial<ProfileLinkConfig>) => void;
  selectedLanguage: string;
  onLanguageChange: (language: string) => void;
  isTranslating: boolean;
  englishIsReadyToUse: boolean;
  swedishIsReadyToUse: boolean;
  onTranslationStart: () => void;
  onTranslationEnd: () => void;
}

const LanguageLinkSettings: FC<LanguageLinkSettingsProps> = ({
  linkConfig,
  onUpdate,
  selectedLanguage,
  onLanguageChange,
  isTranslating,
  englishIsReadyToUse,
  swedishIsReadyToUse,
  onTranslationStart,
  onTranslationEnd,
}) => {
  if (isTranslating) {
    return (
      <div className="p-4 rounded-lg border-1 bg-[linear-gradient(90deg,rgba(255,40,166,0.05)_10%,rgba(57,220,234,0.05)_88%)]">
        <BulletSectionTitle>
          Language
          <p className={cn("text-xs text-gray-6 font-normal break-words")}>
            Translating...
          </p>
        </BulletSectionTitle>
        <div className="flex flex-col items-center text-center relative mt-2">
          <LoadingSpinner size={"x-small"} />
          <p className="text-gray-6 text-sm">
            Translating profile content,
            <br /> this might take a few minutes
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 rounded-2xl border-1 bg-[linear-gradient(90deg,rgba(255,40,166,0.05)_10%,rgba(57,220,234,0.05)_88%)]">
      <Popover>
        <PopoverTrigger className="w-full">
          <div className="flex items-center justify-between gap-2 cursor-pointer hover:opacity-80 transition-opacity">
            <div className="py-2 flex flex-row gap-2 items-center flex-1  min-w-0">
              <img src={MagicIcon} className="w-6 h-6" alt="Magic Icon" />
              <div className="flex flex-col text-left">
                <p className="text-xs font-black text-black">Language</p>
                <p
                  className={cn("text-xs text-gray-6 font-normal break-words")}
                >
                  {selectedLanguage
                    ? capitalizeFirst(selectedLanguage)
                    : "English"}
                </p>
              </div>
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent className="z-[9999] pointer-events-auto">
          <div className="flex flex-col pointer-events-auto">
            <DumbDropdownButtonOption
              onClick={() => {
                onUpdate({
                  language: "english",
                });
                onLanguageChange("english");
                onTranslationStart();
                if (englishIsReadyToUse) {
                  setTimeout(() => {
                    onTranslationEnd();
                  }, 3000);
                }
              }}
              label="English"
              status={
                englishIsReadyToUse
                  ? linkConfig?.language === "english"
                    ? "active"
                    : "ready"
                  : "magic"
              }
            />
            <DumbDropdownButtonOption
              onClick={() => {
                onUpdate({
                  language: "swedish",
                });
                onLanguageChange("swedish");
                onTranslationStart();
                if (swedishIsReadyToUse) {
                  setTimeout(() => {
                    onTranslationEnd();
                  }, 3000);
                }
              }}
              label="Swedish"
              status={
                swedishIsReadyToUse
                  ? linkConfig?.language === "swedish"
                    ? "active"
                    : "ready"
                  : "magic"
              }
            />
          </div>
          <LeadRowCard
            className={cn("text-xs mt-3 w-full bg-red-1 border-red-2")}
            type="dashed"
          >
            <div className="flex flex-1 items-center justify-start gap-3">
              <FontAwesomeIcon
                width={14}
                height={14}
                icon={faCircleExclamation}
                className="w-4 h-4 shrink-0"
              />
              Any custom content changes you made <br />
              in this Lead will be removed when you switch language.
            </div>
          </LeadRowCard>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default LanguageLinkSettings;
